import React from 'react';
import { Button } from '../ui/Button';
import { TOOLBAR_ICONS } from './ToolbarIcons';

// Tool definitions with icon component references
const DRAW_TOOLS = [
  { id: 'select', label: 'Select' },
  { id: 'wall', label: 'Wall' },
  { id: 'door', label: 'Door' },
  { id: 'window', label: 'Window' },
  { id: 'room', label: 'Room' },
  { id: 'roof', label: 'Roof' },
  { id: 'post', label: 'Post' },
];

const MODIFY_TOOLS = [
  { id: 'move', label: 'Move' },
  { id: 'extend', label: 'Extend' },
  { id: 'trim', label: 'Trim' },
  { id: 'break', label: 'Break' },
  { id: 'corner', label: 'Corner' },
  { id: 'chamfer', label: 'Chamfer' },
  { id: 'fillet', label: 'Fillet' },
];

const ANNOTATE_TOOLS = [
  { id: 'dimension', label: 'Dimension' },
  { id: 'text', label: 'Text' },
  { id: 'line', label: 'Line' },
  { id: 'polyline', label: 'Polyline' },
  { id: 'hatch', label: 'Hatch' },
];

const VIEW_TOOLS = [
  { id: 'pan', label: 'Pan' },
  { id: 'zoom-fit', label: 'Fit' },
  { id: '3d', label: '3D View' },
  { id: 'elevations', label: 'Elevations' },
];

const MARKUP_TOOLS = [
  { id: 'annotation-select', label: 'Select' },
  { id: 'annotation-arrow', label: 'Arrow' },
  { id: 'annotation-freehand', label: 'Freehand' },
  { id: 'annotation-circle', label: 'Circle' },
  { id: 'annotation-rectangle', label: 'Rectangle' },
  { id: 'annotation-cloud', label: 'Cloud' },
  { id: 'annotation-stamp', label: 'Stamp', isAction: true },
  { id: 'annotation-callout', label: 'Callout' },
  { id: 'callout-notes', label: 'Notes', isAction: true },
  { id: 'markup-settings', label: 'Settings', isAction: true },
];

const TOOL_TABS = [
  { id: 'draw', label: 'Draw', tools: DRAW_TOOLS },
  { id: 'modify', label: 'Modify', tools: MODIFY_TOOLS },
  { id: 'annotate', label: 'Annotate', tools: ANNOTATE_TOOLS },
  { id: 'view', label: 'View', tools: VIEW_TOOLS },
  { id: 'markup', label: 'Markup', tools: MARKUP_TOOLS },
];

/**
 * SVG Tool Button component
 */
const SvgToolButton = ({ toolId, label, active, onClick, isMobile }) => {
  const IconComponent = TOOLBAR_ICONS[toolId];
  const iconColor = active ? '#00c8ff' : '#8090a0';

  return (
    <Button
      variant="default"
      active={active}
      onClick={onClick}
      style={{
        flexDirection: isMobile ? 'column' : 'row',
        padding: isMobile ? '8px 12px' : '8px 14px',
        minWidth: isMobile ? 'auto' : '80px',
        gap: isMobile ? '2px' : '6px',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {IconComponent ? <IconComponent color={iconColor} /> : toolId}
      </span>
      <span style={{ fontSize: isMobile ? '9px' : '11px' }}>{label}</span>
    </Button>
  );
};

/**
 * Toolbar component with tabbed tools
 */
export const Toolbar = ({
  tool,
  onToolChange,
  toolTab,
  onToolTabChange,
  selectionMode = 'wall',
  onSelectionModeChange,
  onShowStamps,
  onShowAnnotationSettings,
  onShowCalloutNotes,
  isMobile = false,
  style = {},
}) => {
  const currentTabTools = TOOL_TABS.find(t => t.id === toolTab)?.tools || DRAW_TOOLS;

  const handleToolClick = (t) => {
    if (t.isAction) {
      // Handle action buttons
      if (t.id === 'annotation-stamp' && onShowStamps) {
        onShowStamps();
      } else if (t.id === 'markup-settings' && onShowAnnotationSettings) {
        onShowAnnotationSettings();
      } else if (t.id === 'callout-notes' && onShowCalloutNotes) {
        onShowCalloutNotes();
      }
    } else {
      onToolChange(t.id);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg, rgba(15,20,28,0.95) 0%, rgba(8,12,16,0.95) 100%)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        ...style,
      }}
    >
      {/* Tab buttons */}
      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          padding: '4px',
          gap: '4px',
        }}
      >
        {TOOL_TABS.map((tab) => (
          <Button
            key={tab.id}
            variant="ghost"
            active={toolTab === tab.id}
            onClick={() => onToolTabChange(tab.id)}
            style={{
              flex: 1,
              fontSize: '11px',
              padding: '8px 4px',
            }}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Tool buttons */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          padding: '8px',
          gap: '6px',
          justifyContent: isMobile ? 'center' : 'flex-start',
        }}
      >
        {currentTabTools.map((t) => (
          <SvgToolButton
            key={t.id}
            toolId={t.id}
            label={t.label}
            active={!t.isAction && tool === t.id}
            onClick={() => handleToolClick(t)}
            isMobile={isMobile}
          />
        ))}
      </div>

      {/* Selection Mode Toggle - show when select tool is active */}
      {tool === 'select' && onSelectionModeChange && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 12px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(0,0,0,0.2)',
          }}
        >
          <span style={{ fontSize: '11px', color: '#8090a0' }}>Selection:</span>
          <div
            style={{
              display: 'flex',
              background: 'rgba(0,0,0,0.3)',
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            <button
              onClick={() => onSelectionModeChange('wall')}
              style={{
                padding: '4px 12px',
                fontSize: '11px',
                fontWeight: selectionMode === 'wall' ? 600 : 400,
                background: selectionMode === 'wall' ? 'rgba(0,200,255,0.25)' : 'transparent',
                color: selectionMode === 'wall' ? '#00c8ff' : '#8090a0',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              Wall
            </button>
            <button
              onClick={() => onSelectionModeChange('room')}
              style={{
                padding: '4px 12px',
                fontSize: '11px',
                fontWeight: selectionMode === 'room' ? 600 : 400,
                background: selectionMode === 'room' ? 'rgba(0,200,255,0.25)' : 'transparent',
                color: selectionMode === 'room' ? '#00c8ff' : '#8090a0',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              Room
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * MobileToolbar - simplified toolbar for mobile
 * Provides a bottom toolbar with tabbed navigation (Draw/Modify/View/Annotate)
 */
export const MobileToolbar = ({
  tool,
  onToolChange,
  onMenuToggle,
  onUndo,
  onRedo,
  onDelete,
  onCopy,
  onPaste,
  hasClipboard = false,
  pastePending = false,
  canUndo = false,
  canRedo = false,
  hasSelection = false,
  activeTab = 'draw',
  onTabChange,
  onShowLayers,
  onShowSettings,
  onShowFurniture,
  onShow3D,
  onShowElevations,
  onShowStamps,
  onShowAnnotationSettings,
  onShowCalloutNotes,
  onZoomIn,
  onZoomOut,
  onResetView,
  panelOpen = false, // Hide floating buttons when a panel is open
  selectionMode = 'wall',
  onSelectionModeChange,
  hideFloatingButtons = false, // Hide undo/redo when HVAC or Structural toolbar is shown
  style = {},
}) => {
  const [mobileTab, setMobileTab] = React.useState(activeTab);
  const [toolsExpanded, setToolsExpanded] = React.useState(false);

  const handleTabChange = (tab) => {
    // Toggle expansion if same tab clicked, otherwise expand and switch
    if (mobileTab === tab) {
      setToolsExpanded(!toolsExpanded);
    } else {
      setMobileTab(tab);
      setToolsExpanded(true);
      if (onTabChange) onTabChange(tab);
    }
  };

  const handleToolSelect = (toolId, isAction, action) => {
    if (isAction && action) {
      action();
    } else {
      onToolChange(toolId);
      // Collapse after selecting a tool (not for actions like zoom)
      setToolsExpanded(false);
    }
  };

  // Draw tools
  const drawTools = [
    { id: 'select', label: 'Select' },
    { id: 'wall', label: 'Wall' },
    { id: 'door', label: 'Door' },
    { id: 'window', label: 'Window' },
    { id: 'room', label: 'Room' },
    { id: 'roof', label: 'Roof' },
    { id: 'post', label: 'Post' },
    { id: 'furniture', label: 'Furniture', isAction: true, action: onShowFurniture },
  ];

  // Modify tools
  const modifyTools = [
    { id: 'select', label: 'Select' },
    { id: 'move', label: 'Move' },
    { id: 'rotate', label: 'Rotate' },
    { id: 'extend', label: 'Extend' },
    { id: 'trim', label: 'Trim' },
    { id: 'corner', label: 'Corner' },
  ];

  // View tools (actions, not tool modes)
  const viewTools = [
    { id: 'pan', label: 'Pan', isAction: false },
    { id: 'zoomIn', label: 'Zoom+', isAction: true, action: onZoomIn },
    { id: 'zoomOut', label: 'Zoom-', isAction: true, action: onZoomOut },
    { id: 'resetView', label: 'Reset', isAction: true, action: onResetView },
    { id: '3d', label: '3D', isAction: true, action: onShow3D },
    { id: 'elevations', label: 'Elev', isAction: true, action: onShowElevations },
  ];

  // Annotate tools
  const annotateTools = [
    { id: 'dimension', label: 'Dim' },
    { id: 'text', label: 'Text' },
    { id: 'line', label: 'Line' },
    { id: 'polyline', label: 'Poly' },
    { id: 'hatch', label: 'Hatch' },
  ];

  // Markup tools (PDF annotation)
  const markupTools = [
    { id: 'annotation-select', label: 'Select' },
    { id: 'annotation-arrow', label: 'Arrow' },
    { id: 'annotation-freehand', label: 'Draw' },
    { id: 'annotation-circle', label: 'Circle' },
    { id: 'annotation-rectangle', label: 'Rect' },
    { id: 'annotation-cloud', label: 'Cloud' },
    { id: 'annotation-stamp', label: 'Stamp', isAction: true, action: onShowStamps },
    { id: 'annotation-callout', label: 'Callout' },
    { id: 'callout-notes', label: 'Notes', isAction: true, action: onShowCalloutNotes },
    { id: 'markup-settings', label: 'Colors', isAction: true, action: onShowAnnotationSettings },
  ];

  // Tabs configuration with SVG icons
  const tabs = [
    { id: 'draw', label: 'Draw', iconId: 'wall' },
    { id: 'modify', label: 'Modify', iconId: 'move' },
    { id: 'view', label: 'View', iconId: 'pan' },
    { id: 'annotate', label: 'Annotate', iconId: 'dimension' },
    { id: 'markup', label: 'Markup', iconId: 'annotation-freehand' },
  ];

  // Get current tools based on active tab
  const getCurrentTools = () => {
    switch (mobileTab) {
      case 'draw': return drawTools;
      case 'modify': return modifyTools;
      case 'view': return viewTools;
      case 'annotate': return annotateTools;
      case 'markup': return markupTools;
      default: return drawTools;
    }
  };

  const currentTools = getCurrentTools();

  return (
    <>
      {/* Top header bar - minimal */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 10px',
          background: 'linear-gradient(180deg, rgba(15,20,28,0.95) 0%, rgba(8,12,16,0.95) 100%)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          ...style,
        }}
      >
        {/* Menu button */}
        <Button
          variant="ghost"
          onClick={onMenuToggle}
          style={{ padding: '6px' }}
        >
          {(() => {
            const MenuIcon = TOOLBAR_ICONS.menu;
            return MenuIcon ? <MenuIcon color="#8090a0" /> : '☰';
          })()}
        </Button>

        {/* App title */}
        <span style={{ color: '#00c8ff', fontWeight: 'bold', fontSize: '13px' }}>
          FloorPlan Pro
        </span>

        {/* Settings button */}
        <Button
          variant="ghost"
          onClick={onShowSettings}
          style={{ padding: '6px' }}
        >
          {(() => {
            const SettingsIcon = TOOLBAR_ICONS['markup-settings'];
            return SettingsIcon ? <SettingsIcon color="#8090a0" /> : '⚙';
          })()}
        </Button>
      </div>

      {/* Floating action buttons - undo/redo/delete only (hidden when panel is open or HVAC/Structural toolbar shown) */}
      {!panelOpen && !hideFloatingButtons && (
      <div
        style={{
          position: 'fixed',
          top: '52px',
          right: '8px',
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
          // Leaves room for the HVAC/Struct buttons on the left. With copy,
          // paste and delete all showing, the row wraps on a narrow phone
          // instead of overlapping them.
          maxWidth: 'calc(100vw - 190px)',
          gap: '6px',
          zIndex: 150,
        }}
      >
        {onUndo && (() => {
          const UndoIcon = TOOLBAR_ICONS.undo;
          return (
            <Button
              variant="default"
              onClick={onUndo}
              disabled={!canUndo}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '22px',
                padding: 0,
              }}
            >
              {UndoIcon ? <UndoIcon color={canUndo ? '#00c8ff' : '#4a5a6a'} /> : '↩'}
            </Button>
          );
        })()}
        {onRedo && (() => {
          const RedoIcon = TOOLBAR_ICONS.redo;
          return (
            <Button
              variant="default"
              onClick={onRedo}
              disabled={!canRedo}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '22px',
                padding: 0,
              }}
            >
              {RedoIcon ? <RedoIcon color={canRedo ? '#00c8ff' : '#4a5a6a'} /> : '↪'}
            </Button>
          );
        })()}
        {hasSelection && onCopy && (() => {
          const CopyIcon = TOOLBAR_ICONS.copy;
          return (
            <Button
              variant="default"
              onClick={onCopy}
              title="Copy selection"
              aria-label="Copy"
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '22px',
                padding: 0,
              }}
            >
              {CopyIcon ? <CopyIcon color="#00c8ff" /> : '⧉'}
            </Button>
          );
        })()}
        {hasClipboard && onPaste && (() => {
          const PasteIcon = TOOLBAR_ICONS.paste;
          return (
            <Button
              variant="default"
              onClick={onPaste}
              title="Paste"
              aria-label="Paste"
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '22px',
                padding: 0,
                // Stands out while a paste is waiting to be placed
                background: pastePending ? 'rgba(0,200,255,0.25)' : undefined,
                borderColor: pastePending ? 'rgba(0,200,255,0.7)' : undefined,
              }}
            >
              {PasteIcon ? <PasteIcon color="#00c8ff" /> : '📋'}
            </Button>
          );
        })()}
        {hasSelection && onDelete && (() => {
          const DeleteIcon = TOOLBAR_ICONS.delete;
          return (
            <Button
              variant="danger"
              onClick={onDelete}
              title="Delete selection"
              aria-label="Delete"
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '22px',
                padding: 0,
              }}
            >
              {DeleteIcon ? <DeleteIcon color="#ff6666" /> : '🗑'}
            </Button>
          );
        })()}
      </div>
      )}

      {/* Bottom toolbar with tabs */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'rgba(12,18,24,0.98)',
          borderTop: '1px solid rgba(0,200,255,0.15)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 150,
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        {/* Tools row - only show when expanded, compact layout */}
        {toolsExpanded && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-around',
              padding: '6px 4px',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              borderBottom: '1px solid rgba(255,255,255,0.05)',
            }}
          >
            {currentTools.map((t) => {
              const IconComponent = TOOLBAR_ICONS[t.id];
              const isActive = !t.isAction && tool === t.id;
              const iconColor = isActive ? '#00c8ff' : '#8090a0';
              return (
                <Button
                  key={t.id}
                  variant="ghost"
                  active={isActive}
                  onClick={() => handleToolSelect(t.id, t.isAction, t.action)}
                  title={t.label}
                  style={{
                    padding: '6px 8px',
                    minWidth: '48px',
                    flexDirection: 'column',
                    gap: '2px',
                    touchAction: 'manipulation',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {IconComponent ? <IconComponent color={iconColor} /> : t.id}
                  </span>
                  <span style={{ fontSize: '9px' }}>{t.label}</span>
                </Button>
              );
            })}
          </div>
        )}

        {/* Selection Filter - show only when select tool is active */}
        {tool === 'select' && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '6px 12px',
              background: 'rgba(0,0,0,0.3)',
              borderBottom: '1px solid rgba(255,255,255,0.05)',
            }}
          >
            <span style={{ fontSize: '10px', color: '#6080a0' }}>Selection:</span>
            <div
              style={{
                display: 'flex',
                background: 'rgba(0,0,0,0.4)',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <button
                onClick={() => onSelectionModeChange?.('wall')}
                style={{
                  padding: '5px 14px',
                  fontSize: '11px',
                  fontWeight: selectionMode === 'wall' ? 600 : 400,
                  background: selectionMode === 'wall' ? 'rgba(0,200,255,0.25)' : 'transparent',
                  color: selectionMode === 'wall' ? '#00c8ff' : '#8090a0',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Wall
              </button>
              <button
                onClick={() => onSelectionModeChange?.('room')}
                style={{
                  padding: '5px 14px',
                  fontSize: '11px',
                  fontWeight: selectionMode === 'room' ? 600 : 400,
                  background: selectionMode === 'room' ? 'rgba(0,200,255,0.25)' : 'transparent',
                  color: selectionMode === 'room' ? '#00c8ff' : '#8090a0',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Room
              </button>
            </div>
          </div>
        )}

        {/* Tab row - always visible, compact for more drawing space */}
        <div
          style={{
            display: 'flex',
          }}
        >
          {tabs.map((tab) => {
            const TabIconComponent = TOOLBAR_ICONS[tab.iconId];
            const isActive = mobileTab === tab.id;
            const tabIconColor = isActive ? '#00c8ff' : '#6080a0';
            return (
              <Button
                key={tab.id}
                variant="ghost"
                active={isActive}
                onClick={() => handleTabChange(tab.id)}
                style={{
                  flex: 1,
                  padding: '8px 4px',
                  borderRadius: 0,
                  borderTopWidth: '2px',
                  borderTopStyle: 'solid',
                  borderTopColor: (isActive && toolsExpanded) ? '#00c8ff' : 'transparent',
                  flexDirection: 'column',
                  gap: '2px',
                  touchAction: 'manipulation',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {TabIconComponent ? <TabIconComponent color={tabIconColor} /> : tab.id}
                </span>
                <span style={{ fontSize: '10px', fontWeight: isActive ? 'bold' : 'normal' }}>{tab.label}</span>
              </Button>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default Toolbar;
